import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowClockwise,
  DownloadSimple,
  DotsThree,
  DotsSixVertical,
  List,
  PencilSimple,
  Plus,
  Receipt,
  Trash,
  UploadSimple,
  Wallet,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { DashboardLayout } from '../../components/Layout';
import StickyActionHeader from '../../components/StickyActionHeader';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { resolveMediaUrl } from '../../utils/media';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import EventAwareDatePicker from '../../components/EventAwareDatePicker';
import AIPremiumButton from '../../components/AIPremiumGate';
import InfoTip from '../../components/InfoTip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';

const DEFAULT_CURRENCY = 'INR';
const MONEY_ZERO = 0.01;

const isSampleBudgetCategory = (category) => (
  category?.slug === 'sample-category' || category?.name === 'Sample Category'
);

const formatMoney = (amount, currency = DEFAULT_CURRENCY) => {
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(Number(amount || 0));
  } catch (_error) {
    return `${currency} ${Number(amount || 0).toLocaleString('en-IN')}`;
  }
};

const formatDate = (value) => (value ? new Date(value).toLocaleDateString('en-IN') : 'Not set');
const formatCompactDate = (value) => (value ? new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'numeric', year: '2-digit' }) : 'Not set');

const getTodayDateKey = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDueDateStatus = (value) => {
  if (!value) return 'none';
  const due = new Date(value);
  if (Number.isNaN(due.getTime())) return 'none';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  const daysUntilDue = Math.ceil((due.getTime() - today.getTime()) / 86400000);
  if (daysUntilDue < 0) return 'overdue';
  if (daysUntilDue <= 2) return 'soon';
  return 'normal';
};

const getDueSortRank = (record) => {
  const status = getDueDateStatus(record?.due_date);
  if (status === 'overdue') return 0;
  if (status === 'soon') return 1;
  if (status === 'normal') return 2;
  return 3;
};

function DueDateText({ value, highlight = true, compact = false }) {
  const status = highlight ? getDueDateStatus(value) : 'normal';
  const baseClass = compact ? 'inline-flex whitespace-nowrap rounded-full px-1.5 py-[2px] text-[10px] font-medium leading-none' : 'inline-flex whitespace-nowrap rounded-full px-2 py-0.5 font-medium';
  const classes = {
    overdue: `${baseClass} border border-rose-200 bg-rose-50 text-rose-700`,
    soon: `${baseClass} border border-amber-200 bg-amber-50 text-amber-800`,
    normal: compact ? `${baseClass} border border-slate-200 bg-white text-muted-foreground` : '',
    none: '',
  };
  return <span className={classes[status] || ''}>Due {compact ? formatCompactDate(value) : formatDate(value)}</span>;
}

const normalizeTab = (tab, canManage) => {
  if (!canManage) return 'spend';
  if (tab === 'claims' || tab === 'payments') return 'spend';
  if (tab === 'overview') return 'budget';
  return ['budget', 'spend'].includes(tab) ? tab : 'budget';
};

const getDisplayStatus = (record) => {
  const amount = Number(record?.amount || 0);
  const paid = Number(record?.paid_amount || 0);
  if (record?.status === 'needs_information') return { label: 'Needs details', tone: 'missing' };
  if (record?.status === 'awaiting_approval') return { label: 'Needs review', tone: 'wait' };
  if (record?.status === 'rejected') return { label: 'Rejected', tone: 'bad' };
  if (record?.status === 'cancelled') return { label: 'Cancelled', tone: 'muted' };
  if (record?.status === 'paid' || (amount > 0 && paid >= amount - MONEY_ZERO)) return { label: 'Paid', tone: 'good' };
  if (paid > 0) return { label: 'Part paid', tone: 'info' };
  return { label: 'Unpaid', tone: 'info' };
};

const getRecordPerson = (record) => {
  if (record?.record_type === 'team_claim') return record.claimant_name || 'Team member';
  return record?.vendor?.name || record?.claimant_name || 'Manager added';
};

const isNeedsAction = (record) => (
  record.record_type === 'team_claim'
  && ['awaiting_approval', 'needs_information'].includes(record.status)
);

const getOutstandingAmount = (record) => (
  Number(record?.outstanding_amount ?? Math.max(0, Number(record?.amount || 0) - Number(record?.paid_amount || 0)))
);

const isActiveSpendRecord = (record) => (
  ['approved', 'scheduled'].includes(record.status)
  && getOutstandingAmount(record) > MONEY_ZERO
);

function Pill({ record }) {
  const status = getDisplayStatus(record);
  const tones = {
    good: 'bg-emerald-100 text-emerald-700',
    wait: 'bg-amber-100 text-amber-800',
    info: 'bg-sky-100 text-sky-700',
    missing: 'bg-violet-100 text-violet-700',
    bad: 'bg-rose-100 text-rose-700',
    muted: 'bg-slate-100 text-slate-600',
  };
  return <Badge className={tones[status.tone] || tones.muted}>{status.label}</Badge>;
}

function Field({ label, required = false, children }) {
  return (
    <div className="space-y-2">
      <Label>
        {label}
        {required && <span className="ml-0.5 text-rose-600" aria-hidden="true">*</span>}
      </Label>
      {children}
    </div>
  );
}

function Select({ value, onChange, options }) {
  return (
    <select value={value} onChange={(event) => onChange(event.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
      {options.map(([optionValue, label]) => <option key={`${optionValue}-${label}`} value={optionValue}>{label}</option>)}
    </select>
  );
}

function BudgetSnapshot({
  totalBudget,
  spentTillDate,
  pendingRecordingAmount,
  remainingBudget,
  currency,
  totalLabel = 'Total budget',
}) {
  const utilization = totalBudget > 0 ? Math.min(100, Math.max(0, (spentTillDate / totalBudget) * 100)) : 0;
  const stats = [
    { label: totalLabel, value: formatMoney(totalBudget, currency), detail: 'Current event budget' },
    { label: 'Actual spend', value: formatMoney(spentTillDate, currency), detail: 'Recorded payments' },
    {
      label: 'Pending recording',
      value: formatMoney(pendingRecordingAmount, currency),
      detail: 'Planned expenses still left to record as actual spend',
      tone: pendingRecordingAmount > 0 ? 'text-amber-700' : 'text-emerald-700',
    },
    {
      label: 'Remaining',
      value: formatMoney(remainingBudget, currency),
      detail: `${formatMoney(totalBudget, currency)} total`,
      tone: remainingBudget < 0 ? 'text-rose-700' : 'text-emerald-700',
    },
  ];

  return (
    <Card>
      <CardContent className="p-3 sm:p-4">
        <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_180px] lg:items-center">
          <div className="overflow-hidden rounded-xl border bg-white shadow-sm sm:grid sm:gap-2 sm:overflow-visible sm:rounded-none sm:border-0 sm:bg-transparent sm:shadow-none sm:grid-cols-2 xl:grid-cols-4">
            {stats.map((stat) => (
              <div key={stat.label} className="flex items-center justify-between gap-3 border-b px-3 py-2.5 last:border-b-0 sm:block sm:rounded-lg sm:border sm:bg-muted/20 sm:px-3 sm:py-2">
                <div className="flex min-w-0 items-center gap-1">
                  <p className="text-[11px] font-semibold uppercase text-muted-foreground">{stat.label}</p>
                  <InfoTip content={stat.detail} label={`About ${stat.label}`} />
                </div>
                <p className={`shrink-0 text-right text-base font-semibold text-wedding-charcoal sm:mt-1 sm:text-left sm:text-lg ${stat.tone || ''}`}>{stat.value}</p>
              </div>
            ))}
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Budget used</span>
              <span>{Math.round(utilization)}%</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-muted">
              <div
                className={`h-full rounded-full ${remainingBudget < 0 ? 'bg-rose-500' : utilization > 80 ? 'bg-amber-500' : 'bg-wedding-sage'}`}
                style={{ width: `${utilization}%` }}
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function CostCard({ record, currency, canManage, canEdit, onOpen, onEdit, onApprove, onReject, onRequestDetails, onPayment, onDeclineRest }) {
  const teamSubmission = record.record_type === 'team_claim';
  const canReview = canManage && ['awaiting_approval', 'needs_information'].includes(record.status);
  const canPay = canManage && ['approved', 'scheduled'].includes(record.status) && record.outstanding_amount > MONEY_ZERO;
  const canDeclineRest = canPay && Number(record.paid_amount || 0) > MONEY_ZERO;
  const dueStatus = getDueDateStatus(record.due_date);
  const dueIsUrgent = dueStatus === 'overdue' || dueStatus === 'soon';
  return (
    <article className={`overflow-hidden rounded-2xl border bg-[#fffdf9] shadow-[0_12px_30px_rgba(47,42,38,0.07)] ${
      dueStatus === 'overdue'
        ? 'border-rose-300 ring-1 ring-rose-100'
        : dueStatus === 'soon'
          ? 'border-amber-300 ring-1 ring-amber-100'
          : 'border-[#e5d8c9]'
    }`}>
      <div className="grid grid-cols-[minmax(0,1fr)_5.75rem_1.75rem] items-start gap-x-1.5 px-3.5 py-3">
        <button type="button" onClick={() => onOpen(record)} className="min-w-0 text-left">
          <h3 className="truncate text-sm font-semibold text-wedding-charcoal">{record.title || 'Untitled expense'}</h3>
          <span className="mt-2 block min-w-0 truncate text-xs text-muted-foreground">
              {getRecordPerson(record)}
              {record.category?.name ? ` / ${record.category.name}` : ''}
              {teamSubmission ? ' / Team claim' : ''}
            </span>
        </button>
        <button
          type="button"
          onClick={() => onOpen(record)}
          className="flex min-w-0 flex-col items-end gap-1 text-right"
        >
          <strong className="whitespace-nowrap text-base font-semibold tabular-nums text-foreground">{formatMoney(record.amount, currency)}</strong>
          {record.due_date && (
            <span className="flex w-full justify-end">
              <DueDateText value={record.due_date} highlight={dueIsUrgent} compact />
            </span>
          )}
        </button>
        <div className="flex shrink-0 items-start justify-end">
          {canReview && record.status === 'awaiting_approval' && (
            <Button size="sm" className="h-8 px-2.5" onClick={() => onApprove(record)}>Approve</Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="ghost" size="icon" className="h-8 w-8 rounded-full text-muted-foreground hover:bg-muted" aria-label="More expense actions">
                <DotsThree style={{ width: 24, height: 24 }} weight="bold" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => onOpen(record)}>View details</DropdownMenuItem>
              {canEdit && <DropdownMenuItem onSelect={() => onEdit(record)}>Edit</DropdownMenuItem>}
              {record.document_url && (
                <DropdownMenuItem asChild>
                  <a href={resolveMediaUrl(record.document_url)} target="_blank" rel="noreferrer"><Receipt className="h-4 w-4" /> Receipt</a>
                </DropdownMenuItem>
              )}
              {canReview && record.status === 'awaiting_approval' && (
                <>
                  <DropdownMenuItem onSelect={() => onRequestDetails(record)}>Request details</DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => onReject(record)} className="text-red-600">Reject</DropdownMenuItem>
                </>
              )}
              {canDeclineRest && <DropdownMenuItem onSelect={() => onDeclineRest(record)}>Decline remaining</DropdownMenuItem>}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      {canPay && (
        <div className="flex items-center justify-between gap-3 border-t border-[#eee5da] bg-[#f8f4ed] px-3.5 py-2">
          <span className="text-[11px] font-medium uppercase text-muted-foreground">
            {formatMoney(record.outstanding_amount, currency)} left to pay
          </span>
          <Button
            size="sm"
            className="h-7 bg-wedding-sage px-3 text-[11px] text-white shadow-sm hover:bg-wedding-sage/90"
            onClick={() => onPayment(record)}
          >
            Record payment
          </Button>
        </div>
      )}
    </article>
  );
}

function CategoryRow({ category, currency, onEdit, onDragStart, onDragEnd, onDrop, isDragging }) {
  const utilization = Math.min(100, category.utilization_percent || 0);
  const over = (category.utilization_percent || 0) > 100;
  const available = Number(category.available_amount || 0);
  const autoApproveLabel = category.team_auto_approve_limit
    ? `Auto-approve up to ${formatMoney(category.team_auto_approve_limit, currency)}`
    : 'Approval required';
  return (
    <div
      className={`rounded-xl border border-[#e8ded1] bg-white p-3 shadow-sm transition-opacity ${isDragging ? 'opacity-50' : ''}`}
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault();
        onDrop(category);
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 gap-2">
          <button
            type="button"
            draggable
            onDragStart={(event) => {
              event.dataTransfer.effectAllowed = 'move';
              onDragStart(category);
            }}
            onDragEnd={onDragEnd}
            className="mt-0.5 cursor-grab touch-none rounded-md p-0.5 text-muted-foreground hover:bg-muted active:cursor-grabbing"
            title="Drag to reorder"
            aria-label={`Drag ${category.name} to reorder`}
          >
            <DotsSixVertical className="h-5 w-5" />
          </button>
          <div className="min-w-0 space-y-1">
            <div className="flex min-w-0 items-center gap-1.5">
              <p className="truncate text-sm font-semibold text-wedding-charcoal">{category.name}</p>
              <InfoTip content={autoApproveLabel} label={`About ${category.name} approvals`} />
            </div>
            <p className="text-xs text-muted-foreground">{autoApproveLabel}</p>
          </div>
        </div>
        <Button size="icon" variant="outline" onClick={() => onEdit(category)} aria-label={`Edit ${category.name}`} title={`Edit ${category.name}`} className="h-8 w-8 shrink-0">
          <PencilSimple className="h-4 w-4" />
        </Button>
      </div>

      <div className="mt-3 grid grid-cols-3 gap-2">
        <Metric label="Budget" value={formatMoney(category.approved_amount, currency)} />
        <Metric label="Actual" value={formatMoney(category.paid_amount, currency)} />
        <Metric
          label={available < 0 ? 'Over' : 'Left'}
          value={formatMoney(Math.abs(available), currency)}
          className={available < 0 ? 'text-rose-700' : 'text-emerald-700'}
        />
      </div>

      <div className="mt-3 space-y-1.5">
        <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>{formatMoney(category.forecast_amount, currency)} forecast</span>
          <span className={over ? 'font-medium text-rose-700' : ''}>{Math.round(category.utilization_percent || 0)}% used</span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-muted">
          <div className={`h-full rounded-full ${over ? 'bg-rose-500' : utilization > 80 ? 'bg-amber-500' : 'bg-wedding-sage'}`} style={{ width: `${utilization}%` }} />
        </div>
      </div>
      {over && <Badge className="mt-3 bg-rose-100 text-rose-700">Over budget</Badge>}
    </div>
  );
}

function Metric({ label, value, className = '' }) {
  return (
    <div className="min-w-0 rounded-lg border bg-muted/20 px-2.5 py-2">
      <p className="text-[10px] font-semibold uppercase text-muted-foreground">{label}</p>
      <p className={`mt-0.5 truncate text-sm font-semibold ${className}`}>{value}</p>
    </div>
  );
}

function CategoryEditor({ category, currency, busy, onSave, onDelete }) {
  const [values, setValues] = useState({
    name: category?.name || '',
    planned_amount: category?.planned_amount || '',
    approved_amount: category?.approved_amount || '',
    team_auto_approve_limit: category?.team_auto_approve_limit ?? '',
  });

  useEffect(() => {
    setValues({
      name: category?.name || '',
      planned_amount: category?.planned_amount || '',
      approved_amount: category?.approved_amount || '',
      team_auto_approve_limit: category?.team_auto_approve_limit ?? '',
    });
  }, [category]);

  if (!category) return null;

  const payload = {
    name: values.name,
    planned_amount: Number(values.planned_amount || 0),
    approved_amount: Number(values.approved_amount || 0),
    team_auto_approve_limit: values.team_auto_approve_limit === '' ? null : Number(values.team_auto_approve_limit),
  };

  const saveCategory = () => {
    if (!values.name.trim()) {
      toast.error('Enter a category name');
      return;
    }
    if (values.approved_amount === '' || Number(values.approved_amount) <= 0) {
      toast.error('Enter a budget limit');
      return;
    }
    onSave(category, payload);
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg bg-muted/40 p-3 text-sm text-muted-foreground">
        Forecast: {formatMoney(category.forecast_amount, currency)}. Team claims over the category auto-approval limit stay in Needs review.
      </div>
      <Field label="Category name" required><Input required value={values.name} onChange={(event) => setValues({ ...values, name: event.target.value })} /></Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Planned amount"><Input type="number" min="0" value={values.planned_amount} onChange={(event) => setValues({ ...values, planned_amount: event.target.value })} /></Field>
        <Field label="Budget limit" required><Input required type="number" min="1" step="0.01" value={values.approved_amount} onChange={(event) => setValues({ ...values, approved_amount: event.target.value })} /></Field>
      </div>
      <Field label="Auto-approve team claims up to">
        <Input type="number" min="0" placeholder="Leave blank to require approval" value={values.team_auto_approve_limit} onChange={(event) => setValues({ ...values, team_auto_approve_limit: event.target.value })} />
      </Field>
      <DialogFooter className="gap-2 sm:gap-2">
        <Button type="button" variant="outline" onClick={() => onDelete(category)} disabled={busy}>
          <Trash className="mr-2 h-4 w-4" /> Remove
        </Button>
        <Button type="button" onClick={saveCategory} disabled={busy}>Save category</Button>
      </DialogFooter>
    </div>
  );
}

export default function BudgetManagement() {
  const { currentEvent, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [context, setContext] = useState(null);
  const [overview, setOverview] = useState(null);
  const [records, setRecords] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [setupOpen, setSetupOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [expenseMode, setExpenseMode] = useState('planned');
  const [bulkExpenseOpen, setBulkExpenseOpen] = useState(false);
  const [downloadingExpenseTemplate, setDownloadingExpenseTemplate] = useState(false);
  const [uploadingExpenses, setUploadingExpenses] = useState(false);
  const [detailRecord, setDetailRecord] = useState(null);
  const [paymentRecord, setPaymentRecord] = useState(null);
  const [categoryManagerOpen, setCategoryManagerOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [categoryEditing, setCategoryEditing] = useState(null);
  const [draggedCategoryId, setDraggedCategoryId] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [detailDraft, setDetailDraft] = useState({ title: '', amount: '', category_id: '', due_date: '', description: '' });
  const [detailMode, setDetailMode] = useState('view');
  const [requestNote, setRequestNote] = useState('');

  const canManage = Boolean(context?.can_manage);
  const activeTab = normalizeTab(searchParams.get('tab'), canManage);
  const currency = overview?.currency || overview?.plan?.currency || DEFAULT_CURRENCY;
  const autoApprovesOwnSubmission = canManage && user?.role === 'event_manager';
  const currentUserId = user?.id || user?.sub;

  const [setup, setSetup] = useState({
    total_budget: '',
    currency: DEFAULT_CURRENCY,
    guest_count: '',
    contingency_percent: '5',
    auto_approve_limit: '5000',
  });

  const [cost, setCost] = useState({
    source: 'manager',
    file: null,
    title: '',
    amount: '',
    merchant: '',
    description: '',
    category_id: '',
    vendor_id: '',
    itinerary_item_id: '',
    invoice_number: '',
    expense_date: '',
    due_date: '',
    record_type: 'manual_expense',
  });

  const [newCategory, setNewCategory] = useState({
    name: '',
    approved_amount: '',
    team_auto_approve_limit: '',
  });
  const canCreateCategory = newCategory.name.trim() && newCategory.approved_amount !== '' && Number(newCategory.approved_amount) > 0 && busy !== 'category-create';

  const [payment, setPayment] = useState({ amount: '', paid_at: '', method: '', reference: '', note: '' });
  const paymentOutstanding = Number(paymentRecord?.outstanding_amount || 0);
  const paymentAmount = payment.amount === '' ? 0 : Number(payment.amount);
  const paymentAmountError = payment.amount !== '' && (
    Number.isNaN(paymentAmount)
      ? 'Enter a valid payment amount.'
      : paymentAmount <= 0
        ? 'Payment amount must be greater than zero.'
        : ''
  );
  const paymentOverage = payment.amount !== '' && !paymentAmountError && paymentOutstanding > 0 && paymentAmount > paymentOutstanding
    ? paymentAmount - paymentOutstanding
    : 0;
  const canSubmitPayment = payment.amount !== '' && !paymentAmountError && busy !== 'payment';

  const loadData = useCallback(async () => {
    if (!currentEvent?.id) return;
    setLoading(true);
    try {
      const contextResponse = await api.get(`/events/${currentEvent.id}/budget/context`);
      setContext(contextResponse.data);
      const recordsRequest = api.get(`/events/${currentEvent.id}/budget/records`);
      const paymentsRequest = api.get(`/events/${currentEvent.id}/budget/payments`);
      if (contextResponse.data.can_manage) {
        const [overviewResponse, recordsResponse, paymentsResponse] = await Promise.all([
          api.get(`/events/${currentEvent.id}/budget/overview`),
          recordsRequest,
          paymentsRequest,
        ]);
        setOverview(overviewResponse.data);
        setRecords(recordsResponse.data || []);
        setPayments(paymentsResponse.data || []);
        if (overviewResponse.data.configured) {
          setSetup((current) => ({
            ...current,
            total_budget: overviewResponse.data.plan.total_budget,
            currency: overviewResponse.data.currency,
            guest_count: overviewResponse.data.plan.guest_count || '',
            contingency_percent: overviewResponse.data.plan.contingency_percent,
            auto_approve_limit: overviewResponse.data.plan.auto_approve_limit,
          }));
        } else {
          setSetupOpen(true);
        }
      } else {
        const [recordsResponse, paymentsResponse] = await Promise.all([recordsRequest, paymentsRequest]);
        setRecords(recordsResponse.data || []);
        setPayments(paymentsResponse.data || []);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not load the budget workspace');
    } finally {
      setLoading(false);
    }
  }, [currentEvent?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!canManage && activeTab !== 'spend') setSearchParams({ tab: 'spend' });
  }, [activeTab, canManage, setSearchParams]);

  const attentionRecords = useMemo(() => records.filter(isNeedsAction), [records]);
  const spendRecords = useMemo(
    () => records
      .filter(isActiveSpendRecord)
      .sort((first, second) => {
        const rankDiff = getDueSortRank(first) - getDueSortRank(second);
        if (rankDiff !== 0) return rankDiff;
        const firstDue = first.due_date ? new Date(first.due_date).getTime() : Number.MAX_SAFE_INTEGER;
        const secondDue = second.due_date ? new Date(second.due_date).getTime() : Number.MAX_SAFE_INTEGER;
        if (firstDue !== secondDue) return firstDue - secondDue;
        return (first.title || '').localeCompare(second.title || '');
      }),
    [records],
  );

  const recordsById = useMemo(() => (
    records.reduce((lookup, record) => ({ ...lookup, [record.id]: record }), {})
  ), [records]);

  const filteredPayments = useMemo(() => (
    payments.filter((paymentItem) => {
      const record = recordsById[paymentItem.record_id];
      if (paymentFilter === 'team') return record?.record_type === 'team_claim';
      if (paymentFilter === 'manager') return record && record.record_type !== 'team_claim';
      return true;
    })
  ), [paymentFilter, payments, recordsById]);

  const categoryOptions = useMemo(
    () => (context?.categories || []).filter((category) => !isSampleBudgetCategory(category)),
    [context?.categories],
  );
  const overviewCategories = useMemo(
    () => (overview?.categories || []).filter((category) => !isSampleBudgetCategory(category)),
    [overview?.categories],
  );
  const totalBudget = Number(overview?.total_budget ?? overview?.plan?.total_budget ?? 0);
  const spentTillDate = Number(overview?.paid || 0);
  const pendingRecordingAmount = useMemo(
    () => spendRecords.reduce((total, record) => total + getOutstandingAmount(record), 0),
    [spendRecords],
  );
  const remainingBudget = Number(overview?.available ?? totalBudget - Number(overview?.forecast || 0));
  const totalBudgetLabel = currentEvent?.event_type === 'birthday' ? 'Birthday budget' : 'Total budget';
  const todayDateKey = getTodayDateKey();
  const isActualExpenseMode = canManage && expenseMode === 'actual';
  const addExpenseTitle = canManage
    ? isActualExpenseMode ? 'Add actual expense' : 'Add planned expense'
    : 'Submit expense';
  const addExpenseDescription = canManage
    ? isActualExpenseMode
      ? 'Record an expense that has already been paid. It will appear under Actual spend.'
      : 'Record an upcoming or unpaid expense. It will appear under Future expenses.'
    : 'Team expenses go to manager review unless category rules auto-approve them.';

  const changeTab = (value) => {
    setSearchParams({ tab: value });
  };

  const openBudgetSetup = () => {
    const plan = overview?.plan;
    if (plan) {
      setSetup({
        total_budget: plan.total_budget ?? '',
        currency: overview?.currency || plan.currency || DEFAULT_CURRENCY,
        guest_count: plan.guest_count ?? '',
        contingency_percent: plan.contingency_percent ?? '5',
        auto_approve_limit: plan.auto_approve_limit ?? '5000',
      });
    }
    setSetupOpen(true);
  };

  const submitSetup = async (event) => {
    event.preventDefault();
    try {
      setBusy('setup');
      await api.post(`/events/${currentEvent.id}/budget/setup`, {
        ...setup,
        total_budget: Number(setup.total_budget),
        guest_count: Number(setup.guest_count || 0),
        contingency_percent: Number(setup.contingency_percent),
        auto_approve_limit: Number(setup.auto_approve_limit),
      });
      toast.success(overview?.configured ? 'Budget updated' : 'Budget plan generated');
      setSetupOpen(false);
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not create the budget');
    } finally {
      setBusy('');
    }
  };

  const resetCost = () => {
    setCost({
      source: canManage ? 'manager' : 'team',
      file: null,
      title: '',
      amount: '',
      merchant: '',
      description: '',
      category_id: '',
      vendor_id: '',
      itinerary_item_id: '',
      invoice_number: '',
      expense_date: todayDateKey,
      due_date: todayDateKey,
      record_type: 'manual_expense',
    });
  };

  const openAddCost = (mode = 'planned') => {
    setExpenseMode(mode);
    resetCost();
    setAddOpen(true);
  };

  const openAddCategoryFromExpense = () => {
    setCategoryEditing(null);
    setNewCategory({ name: '', approved_amount: '', team_auto_approve_limit: '' });
    setCategoryDialogOpen(true);
  };

  const openDetail = (record, mode = 'view') => {
    setDetailRecord(record);
    setDetailMode(mode);
    setRequestNote('');
    setDetailDraft({
      title: record.title || '',
      amount: record.amount || '',
      category_id: record.category_id || '',
      due_date: record.due_date ? new Date(record.due_date).toISOString().slice(0, 10) : '',
      description: record.description || '',
    });
  };

  const openEditDetail = (record) => openDetail(record, 'edit');

  const submitCost = async (event) => {
    event.preventDefault();
    const isDocumentSubmission = Boolean(cost.file);
    const isActualExpense = canManage && expenseMode === 'actual';
    if (!canManage && (!cost.title.trim() || Number(cost.amount) <= 0 || !cost.expense_date)) {
      toast.error('Enter an expense name, amount, and expense date');
      return;
    }
    if (!canManage && cost.expense_date < todayDateKey) {
      toast.error('Expense date cannot be in the past');
      return;
    }
    if (canManage && (isActualExpense || !isDocumentSubmission) && (!cost.title.trim() || Number(cost.amount) <= 0)) {
      toast.error(isActualExpense ? 'Enter an expense name and amount' : 'Enter a title and amount');
      return;
    }

    try {
      setBusy('cost');
      let savedRecord = null;
      if (isDocumentSubmission || !canManage) {
        const formData = new FormData();
        if (cost.file) formData.append('file', cost.file);
        formData.append('claim_type', cost.source === 'team' ? 'reimbursement' : 'vendor_invoice');
        if (cost.amount !== '') formData.append('amount', cost.amount);
        if (cost.title || cost.merchant) formData.append('merchant', canManage ? cost.merchant || cost.title : cost.title);
        if (cost.description) formData.append('description', cost.description);
        if (cost.category_id) formData.append('category_id', cost.category_id);
        if (cost.vendor_id) formData.append('vendor_id', cost.vendor_id);
        if (cost.itinerary_item_id) formData.append('itinerary_item_id', cost.itinerary_item_id);
        if (cost.invoice_number) formData.append('invoice_number', cost.invoice_number);
        if (cost.expense_date) formData.append('expense_date', cost.expense_date);
        if (!isActualExpense && cost.due_date) formData.append('due_date', cost.due_date);
        const response = await api.post(`/events/${currentEvent.id}/budget/claims`, formData);
        savedRecord = response.data;
        const automation = response.data.automation || {};
        if (!isActualExpense && response.data.status === 'needs_information') {
          toast.info(isDocumentSubmission
            ? 'Receipt saved. Missing details can be completed later.'
            : 'Expense saved. Missing details can be completed later.');
        } else if (!isActualExpense) {
          toast.success(automation.auto_approved ? 'Planned expense added' : 'Expense submitted for review');
        }
        if (automation.duplicate_record_id) toast.info('A possible duplicate was detected and held for review');
      } else {
        const response = await api.post(`/events/${currentEvent.id}/budget/records`, {
          record_type: cost.record_type,
          title: cost.title,
          amount: Number(cost.amount),
          category_id: cost.category_id || null,
          vendor_id: cost.vendor_id || null,
          itinerary_item_id: cost.itinerary_item_id || null,
          invoice_number: cost.invoice_number || null,
          due_date: !isActualExpense && cost.due_date ? new Date(cost.due_date).toISOString() : null,
          description: cost.description || null,
        });
        savedRecord = response.data;
        if (!isActualExpense) {
          toast.success('Planned expense added');
        }
      }
      if (isActualExpense && savedRecord?.id) {
        await api.post(`/events/${currentEvent.id}/budget/records/${savedRecord.id}/payments`, {
          amount: Number(cost.amount || savedRecord.amount),
          paid_at: cost.expense_date ? new Date(cost.expense_date).toISOString() : new Date().toISOString(),
          reference: cost.invoice_number || '',
          note: cost.description || '',
        });
        toast.success('Actual expense added');
      }
      setAddOpen(false);
      resetCost();
      await loadData();
      changeTab('spend');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not add the expense');
    } finally {
      setBusy('');
    }
  };

  const downloadExpenseTemplate = async () => {
    if (!currentEvent?.id) return;
    try {
      setDownloadingExpenseTemplate(true);
      const response = await api.get(
        `/events/${currentEvent.id}/budget/claims/bulk-template`,
        { responseType: 'blob' },
      );
      const disposition = response.headers['content-disposition'] || '';
      const filename = disposition.match(/filename="?([^"]+)"?/i)?.[1]
        || `expense_bulk_template_${currentEvent.id}.xlsx`;
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Expense template downloaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not download the expense template');
    } finally {
      setDownloadingExpenseTemplate(false);
    }
  };

  const uploadExpenseWorkbook = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !currentEvent?.id) return;
    try {
      setUploadingExpenses(true);
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(
        `/events/${currentEvent.id}/budget/claims/bulk`,
        formData,
      );
      const added = response.data?.expenses_added || 0;
      const skipped = response.data?.skipped || [];
      const skippedSummary = skipped
        .slice(0, 4)
        .map((item) => `Row ${item.row}: ${item.reason}`)
        .join('\n');
      if (added === 0) {
        toast.error('No expenses were added', { description: skippedSummary || undefined });
      } else if (skipped.length > 0) {
        toast.warning(response.data.message, { description: skippedSummary });
      } else {
        toast.success(response.data.message);
      }
      if (added > 0) {
        setBulkExpenseOpen(false);
        await loadData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not upload expenses');
    } finally {
      setUploadingExpenses(false);
      event.target.value = '';
    }
  };

  const updateStatus = async (record, status, note = '') => {
    try {
      setBusy(record.id);
      await api.put(`/events/${currentEvent.id}/budget/records/${record.id}/status`, { status, note: note || null });
      toast.success(status === 'approved' ? 'Expense approved' : status === 'needs_information' ? 'Sent back for details' : 'Expense updated');
      setDetailRecord(null);
      setRequestNote('');
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not update approval');
    } finally {
      setBusy('');
    }
  };

  const requestDetails = async (record, note = '') => {
    await updateStatus(record, 'needs_information', note || 'Please add the missing details and resend for approval.');
  };

  const canEditDetails = (record) => {
    if (!record) return false;
    if (!canManage) {
      return record.status === 'needs_information' && record.claimant_user_id === currentUserId;
    }
    return isActiveSpendRecord(record);
  };

  const saveRecordDetails = async ({ submitForApproval = false } = {}) => {
    if (!detailRecord) return;
    try {
      setBusy('details');
      const response = await api.put(`/events/${currentEvent.id}/budget/records/${detailRecord.id}`, {
        title: detailDraft.title,
        amount: Number(detailDraft.amount || 0),
        category_id: detailDraft.category_id || null,
        due_date: detailDraft.due_date ? new Date(detailDraft.due_date).toISOString() : null,
        description: detailDraft.description,
        submit_for_approval: submitForApproval,
      });
      toast.success(submitForApproval ? 'Sent back for approval' : 'Expense details saved');
      setDetailRecord(response.data);
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not save details');
    } finally {
      setBusy('');
    }
  };

  const openPaymentDialog = (record) => {
    const outstandingAmount = getOutstandingAmount(record);
    setPaymentRecord(record);
    setPayment({ amount: outstandingAmount > MONEY_ZERO ? String(outstandingAmount) : '', paid_at: '', method: '', reference: '', note: '' });
  };

  const closePaymentDialog = () => {
    setPaymentRecord(null);
    setPayment({ amount: '', paid_at: '', method: '', reference: '', note: '' });
  };

  const submitPayment = async (event) => {
    event.preventDefault();
    if (!paymentRecord) return;
    if (!canSubmitPayment) {
      toast.error(paymentAmountError || 'Enter a payment amount');
      return;
    }
    try {
      setBusy('payment');
      await api.post(`/events/${currentEvent.id}/budget/records/${paymentRecord.id}/payments`, {
        ...payment,
        amount: Number(payment.amount),
        paid_at: payment.paid_at ? new Date(payment.paid_at).toISOString() : new Date().toISOString(),
      });
      toast.success('Payment recorded under Actual spend');
      closePaymentDialog();
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not record the payment');
    } finally {
      setBusy('');
    }
  };

  const declineRemaining = async (record) => {
    try {
      setBusy(record.id);
      await api.put(`/events/${currentEvent.id}/budget/records/${record.id}/decline-remainder`);
      toast.success('Remaining balance declined');
      setDetailRecord(null);
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not decline the remaining balance');
    } finally {
      setBusy('');
    }
  };

  const saveCategory = async (category, values) => {
    try {
      setBusy(category.id);
      await api.put(`/events/${currentEvent.id}/budget/categories/${category.id}`, values);
      toast.success(`${category.name} updated`);
      setCategoryEditing(null);
      setCategoryDialogOpen(false);
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not update the category');
    } finally {
      setBusy('');
    }
  };

  const reorderCategories = async (targetCategory) => {
    if (!draggedCategoryId || draggedCategoryId === targetCategory.id) {
      setDraggedCategoryId('');
      return;
    }
    const currentCategories = overviewCategories;
    const sourceIndex = currentCategories.findIndex((category) => category.id === draggedCategoryId);
    const targetIndex = currentCategories.findIndex((category) => category.id === targetCategory.id);
    if (sourceIndex < 0 || targetIndex < 0) {
      setDraggedCategoryId('');
      return;
    }

    const reordered = [...currentCategories];
    const [moved] = reordered.splice(sourceIndex, 1);
    reordered.splice(targetIndex, 0, moved);
    setOverview((current) => ({
      ...current,
      categories: reordered.map((category, index) => ({ ...category, sort_order: index })),
    }));
    setDraggedCategoryId('');
    try {
      await api.put(`/events/${currentEvent.id}/budget/categories/order`, {
        category_ids: reordered.map((category) => category.id),
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not reorder categories');
      await loadData();
    }
  };

  const createCategory = async () => {
    if (!newCategory.name.trim()) {
      toast.error('Enter a category name');
      return;
    }
    if (newCategory.approved_amount === '' || Number(newCategory.approved_amount) <= 0) {
      toast.error('Enter a budget limit');
      return;
    }
    try {
      setBusy('category-create');
      const response = await api.post(`/events/${currentEvent.id}/budget/categories`, {
        name: newCategory.name.trim(),
        planned_amount: Number(newCategory.approved_amount || 0),
        approved_amount: Number(newCategory.approved_amount || 0),
        team_auto_approve_limit: newCategory.team_auto_approve_limit === '' ? null : Number(newCategory.team_auto_approve_limit),
      });
      toast.success('Category added');
      if (addOpen && response.data?.id) {
        setCost((current) => ({ ...current, category_id: response.data.id }));
      }
      setNewCategory({ name: '', approved_amount: '', team_auto_approve_limit: '' });
      setCategoryDialogOpen(false);
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not add category');
    } finally {
      setBusy('');
    }
  };

  const deleteCategory = async (category) => {
    try {
      setBusy(category.id);
      await api.delete(`/events/${currentEvent.id}/budget/categories/${category.id}`);
      toast.success(`${category.name} removed`);
      setCategoryEditing(null);
      setCategoryDialogOpen(false);
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not remove category');
    } finally {
      setBusy('');
    }
  };

  const downloadReport = async () => {
    try {
      const response = await api.get(`/events/${currentEvent.id}/budget/export`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${currentEvent.name || 'event'}-budget.csv`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Could not download the budget report');
    }
  };

  if (loading) {
    return <DashboardLayout><div className="h-96 animate-pulse rounded-2xl bg-muted" /></DashboardLayout>;
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <StickyActionHeader>
          <div className="flex flex-wrap items-center gap-1">
            <h1 className="font-serif text-3xl font-bold text-wedding-charcoal">{canManage ? 'Budget' : 'My Expenses'}</h1>
            <InfoTip
              content={canManage ? 'Track budget, expenses, team reimbursements, payments, and client reporting.' : 'Submit expenses and track approval and payment status.'}
              label={canManage ? 'About budget management' : 'About my expenses'}
            />
            {canManage && (
              <span className="ml-1 hidden rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground sm:inline-flex">
                This budget is only visible to you
              </span>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-1.5 sm:gap-2" data-header-action-group>
            <Button size="icon" variant="outline" onClick={loadData} aria-label="Refresh budget data" title="Refresh budget data" data-header-action>
              <ArrowClockwise className="h-4 w-4" />
            </Button>
            {canManage && overview?.configured && (
              <Button size="icon" variant="outline" onClick={openBudgetSetup} aria-label="Edit budget" title="Edit budget" data-header-action>
                <PencilSimple className="h-4 w-4" />
              </Button>
            )}
            {canManage && overview?.configured && (
              <Button size="icon" variant="outline" onClick={downloadReport} aria-label="Download budget report" title="Download budget report" data-header-action>
                <DownloadSimple className="h-4 w-4" />
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="gap-2 px-3" aria-label={canManage ? 'Add expense options' : 'Submit expense options'} title={canManage ? 'Add expense options' : 'Submit expense options'} data-header-action>
                  <Plus className="h-4 w-4" />
                  <span className="hidden sm:inline">{canManage ? 'Add expense' : 'Expense'}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canManage ? (
                  <>
                    <DropdownMenuItem onSelect={() => openAddCost('planned')}>
                      <Plus className="h-4 w-4" /> Add planned expense
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => openAddCost('actual')}>
                      <Wallet className="h-4 w-4" /> Add actual expense
                    </DropdownMenuItem>
                  </>
                ) : (
                  <DropdownMenuItem onSelect={() => openAddCost('planned')}>
                    <Plus className="h-4 w-4" /> Submit expense
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onSelect={() => setBulkExpenseOpen(true)}>
                  <UploadSimple className="h-4 w-4" /> Import expenses
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {canManage && overview?.configured && (
              <Button size="icon" variant="outline" onClick={() => setCategoryManagerOpen(true)} aria-label="Manage categories" title="Manage categories" className="ml-auto" data-header-action>
                <List className="h-4 w-4" />
              </Button>
            )}
          </div>
        </StickyActionHeader>

        <Tabs value={activeTab} onValueChange={changeTab}>
          <TabsList className={`grid h-auto w-full ${canManage ? 'grid-cols-2' : 'grid-cols-1'}`}>
            {canManage && <TabsTrigger value="budget">Budget</TabsTrigger>}
            <TabsTrigger value="spend">{canManage ? 'Future expenses' : 'My Expenses'}</TabsTrigger>
          </TabsList>

          {canManage && (
            <TabsContent value="budget" className="space-y-6">
              {!overview?.configured ? (
                <Card className="border-wedding-gold/40 bg-gradient-to-br from-amber-50 to-white">
                  <CardContent className="py-12 text-center">
                    <Wallet className="mx-auto h-12 w-12 text-wedding-sage" />
                    <div className="mt-4 flex items-center justify-center gap-1">
                      <h2 className="font-serif text-2xl">Create the event budget</h2>
                      <InfoTip content="Set the total once. Add categories from the category manager when you are ready to allocate the budget." label="About budget setup" />
                    </div>
                    <Button className="mt-5" onClick={openBudgetSetup}>Start budget setup</Button>
                  </CardContent>
                </Card>
              ) : (
                <>
                  <BudgetSnapshot
                    totalBudget={totalBudget}
                    spentTillDate={spentTillDate}
                    pendingRecordingAmount={pendingRecordingAmount}
                    remainingBudget={remainingBudget}
                    currency={currency}
                    totalLabel={totalBudgetLabel}
                  />

                  <Card>
                    <CardHeader className="flex-row items-center justify-between gap-3">
                      <div className="flex items-center gap-1">
                        <CardTitle className="font-serif">Needs attention</CardTitle>
                        <InfoTip content="Only team expenses awaiting review or missing details appear here." label="About items needing attention" />
                      </div>
                      <Button size="sm" variant="outline" onClick={() => changeTab('spend')}>Review</Button>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {attentionRecords.slice(0, 4).map((record) => (
                        <CostCard key={record.id} record={record} currency={currency} canManage={canManage} canEdit={canEditDetails(record)} onOpen={openDetail} onEdit={openEditDetail} onApprove={(item) => updateStatus(item, 'approved')} onReject={(item) => updateStatus(item, 'rejected')} onRequestDetails={requestDetails} onPayment={openPaymentDialog} onDeclineRest={declineRemaining} />
                      ))}
                      {!attentionRecords.length && <p className="rounded-xl border border-dashed py-10 text-center text-sm text-muted-foreground">No team members have requested any claims</p>}
                    </CardContent>
                  </Card>
                </>
              )}
            </TabsContent>
          )}

          <TabsContent value="spend" className="space-y-5">
            {!canManage && (
              <Card>
                <CardHeader className="gap-4 md:flex-row md:items-center md:justify-between">
                  <div className="flex items-center gap-1">
                    <CardTitle className="font-serif">My submitted expenses</CardTitle>
                    <InfoTip content="Returned claims can be opened, completed, and resent for approval." label="About submitted expenses" />
                  </div>
                </CardHeader>
                <CardContent className="space-y-2.5">
                  {records.map((record) => (
                    <CostCard key={record.id} record={record} currency={currency} canManage={false} canEdit={canEditDetails(record)} onOpen={openDetail} onEdit={openEditDetail} onApprove={() => {}} onReject={() => {}} onRequestDetails={() => {}} onPayment={() => {}} onDeclineRest={() => {}} />
                  ))}
                  {!records.length && <p className="rounded-xl border border-dashed py-10 text-center text-sm text-muted-foreground">No expenses submitted yet.</p>}
                </CardContent>
              </Card>
            )}
            {canManage && (
              <Card>
                <CardHeader className="gap-4 md:flex-row md:items-center md:justify-between">
                  <div className="flex items-center gap-1">
                    <CardTitle className="font-serif">Future expenses</CardTitle>
                    <InfoTip content="Approved expenses with money still left to pay appear here." label="About future expenses" />
                  </div>
                </CardHeader>
                <CardContent className="space-y-2.5">
                  {spendRecords.map((record) => (
                    <CostCard key={record.id} record={record} currency={currency} canManage={canManage} canEdit={canEditDetails(record)} onOpen={openDetail} onEdit={openEditDetail} onApprove={(item) => updateStatus(item, 'approved')} onReject={(item) => updateStatus(item, 'rejected')} onRequestDetails={requestDetails} onPayment={openPaymentDialog} onDeclineRest={declineRemaining} />
                  ))}
                  {!spendRecords.length && <p className="rounded-xl border border-dashed py-10 text-center text-sm text-muted-foreground">No future expenses right now.</p>}
                </CardContent>
              </Card>
            )}
            <Card>
              <CardHeader className="gap-4 md:flex-row md:items-center md:justify-between">
                <div className="flex items-center gap-1">
                  <CardTitle className="font-serif">{canManage ? 'Actual spend' : 'My Actual spend'}</CardTitle>
                  <InfoTip content="Money already paid against future expenses appears here." label="About actual spend" />
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  {[
                    ['all', 'All actual spend'],
                    ['team', 'Team'],
                    ['manager', canManage ? 'Manager added' : 'Myself'],
                  ].filter(([value]) => canManage || value !== 'team').map(([value, label]) => (
                    <Button key={value} type="button" size="sm" variant={paymentFilter === value ? 'default' : 'outline'} onClick={() => setPaymentFilter(value)}>{label}</Button>
                  ))}
                </div>
                <div className="space-y-3">
                  {filteredPayments.map((paymentItem) => {
                    const record = recordsById[paymentItem.record_id];
                    return (
                      <article key={paymentItem.id} className="rounded-xl border border-[#e8e1d8] bg-white px-3.5 py-3 shadow-[0_8px_18px_rgba(47,42,38,0.04)]">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <h3 className="truncate text-sm font-semibold text-wedding-charcoal">{record?.title || 'Recorded actual spend'}</h3>
                            <p className="mt-1 truncate text-xs text-muted-foreground">
                              {paymentItem.method || 'Payment'} / {formatDate(paymentItem.paid_at)}
                            </p>
                            {paymentItem.reference && <p className="mt-1 truncate text-xs text-muted-foreground">Ref {paymentItem.reference}</p>}
                          </div>
                          <strong className="shrink-0 text-base font-semibold tabular-nums text-foreground">{formatMoney(paymentItem.amount, currency)}</strong>
                        </div>
                        <div className="mt-2 flex items-center justify-between gap-3">
                          <span className="truncate text-xs text-muted-foreground">{record ? getRecordPerson(record) : 'Future expense record'}</span>
                          {record && (
                            <button type="button" className="shrink-0 text-xs font-medium text-wedding-sage hover:underline" onClick={() => openDetail(record)}>
                              View future expense
                            </button>
                          )}
                        </div>
                      </article>
                    );
                  })}
                  {!filteredPayments.length && <p className="rounded-xl border border-dashed py-12 text-center text-sm text-muted-foreground">No actual spend matches this filter.</p>}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

        </Tabs>
      </div>

      <Dialog open={setupOpen} onOpenChange={setSetupOpen}>
        <DialogContent className="max-w-2xl">
          <form onSubmit={submitSetup}>
            <DialogHeader>
              <DialogTitle>{overview?.configured ? 'Edit budget' : 'Budget setup'}</DialogTitle>
              <DialogDescription>
                {overview?.configured
                  ? 'Update the event budget settings. Existing expenses, payments, and categories are preserved.'
                  : 'Set the baseline. You can add categories after the budget is saved.'}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-5 sm:grid-cols-2">
              <Field label="Total event budget" required><Input type="number" min="1" required value={setup.total_budget} onChange={(event) => setSetup({ ...setup, total_budget: event.target.value })} /></Field>
              <Field label="Currency" required><Input maxLength={3} required value={setup.currency} onChange={(event) => setSetup({ ...setup, currency: event.target.value.toUpperCase() })} /></Field>
              <Field label="Expected guests"><Input type="number" min="0" value={setup.guest_count} onChange={(event) => setSetup({ ...setup, guest_count: event.target.value })} /></Field>
              <Field label="Contingency %"><Input type="number" min="0" max="30" value={setup.contingency_percent} onChange={(event) => setSetup({ ...setup, contingency_percent: event.target.value })} /></Field>
              <Field label="Default auto-approve up to"><Input type="number" min="0" value={setup.auto_approve_limit} onChange={(event) => setSetup({ ...setup, auto_approve_limit: event.target.value })} /></Field>
            </div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setSetupOpen(false)}>Cancel</Button><Button type="submit" disabled={busy === 'setup'}>{busy === 'setup' ? 'Saving...' : overview?.configured ? 'Update budget' : 'Save budget'}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto">
          <form onSubmit={submitCost}>
            <DialogHeader>
              <DialogTitle>{addExpenseTitle}</DialogTitle>
              <DialogDescription>
                {addExpenseDescription}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-5 py-5">
              <section className="rounded-2xl border border-violet-200 bg-violet-50/60 p-4">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 space-y-1">
                    <p className="text-sm font-semibold text-violet-950">Scan receipt for details</p>
                    <p className="text-sm text-violet-900">
                      Upload a receipt or invoice to help fill amount, title, category, and duplicate checks.
                    </p>
                  </div>
                  <AIPremiumButton featureName="AI invoice auto-fill" variant="outline" className="shrink-0 bg-white">
                    Scan receipt
                  </AIPremiumButton>
                </div>
                <div className="mt-4">
                  <Field label={canManage ? 'Receipt or invoice' : 'Receipt (optional)'}>
                    <Input type="file" accept="application/pdf,image/jpeg,image/png,image/webp" onChange={(event) => setCost({ ...cost, file: event.target.files?.[0] || null })} />
                  </Field>
                </div>
              </section>

              <section className="grid gap-4 sm:grid-cols-2">
                {canManage ? (
                  <>
                    <Field label="What is it for?" required={isActualExpenseMode || !cost.file}><Input required={isActualExpenseMode || !cost.file} value={cost.title} onChange={(event) => setCost({ ...cost, title: event.target.value })} /></Field>
                    <Field label="Amount" required={isActualExpenseMode || !cost.file}><Input required={isActualExpenseMode || !cost.file} type="number" min="0.01" step="0.01" value={cost.amount} onChange={(event) => setCost({ ...cost, amount: event.target.value })} placeholder={isActualExpenseMode ? '' : 'Optional if receipt can be read'} /></Field>
                    <Field label="Category">
                      <Select
                        value={cost.category_id}
                        onChange={(value) => setCost({ ...cost, category_id: value })}
                        options={[['', categoryOptions.length ? 'Suggest automatically' : 'Add a category first'], ...categoryOptions.map((item) => [item.id, item.name])]}
                      />
                      <button
                        type="button"
                        className="mt-1.5 inline-flex items-center text-xs font-medium text-wedding-sage hover:underline"
                        onClick={openAddCategoryFromExpense}
                      >
                        <Plus className="mr-1 h-3.5 w-3.5" /> Add category
                      </button>
                    </Field>
                    {isActualExpenseMode ? (
                      <Field label="Payment date">
                        <EventAwareDatePicker value={cost.expense_date} onChange={(date) => setCost({ ...cost, expense_date: date })} defaultDate={todayDateKey} />
                      </Field>
                    ) : (
                      <Field label="Due date">
                        <EventAwareDatePicker value={cost.due_date} onChange={(date) => setCost({ ...cost, due_date: date })} defaultDate={todayDateKey} minDate={todayDateKey} />
                      </Field>
                    )}
                    <Field label="Invoice number"><Input value={cost.invoice_number} onChange={(event) => setCost({ ...cost, invoice_number: event.target.value })} /></Field>
                  </>
                ) : (
                  <>
                    <Field label="Expense name" required>
                      <Input required value={cost.title} onChange={(event) => setCost({ ...cost, title: event.target.value })} />
                    </Field>
                    <Field label="Amount" required><Input required type="number" min="0.01" step="0.01" value={cost.amount} onChange={(event) => setCost({ ...cost, amount: event.target.value })} /></Field>
                    <Field label="Expense date" required>
                      <Input required type="date" min={todayDateKey} value={cost.expense_date} onChange={(event) => setCost({ ...cost, expense_date: event.target.value })} />
                    </Field>
                    <Field label="Event name"><Select value={cost.itinerary_item_id} onChange={(value) => setCost({ ...cost, itinerary_item_id: value })} options={[['', 'Select an event (optional)'], ...(context?.itinerary || []).map((item) => [item.id, item.name])]} /></Field>
                  </>
                )}
                <div className="sm:col-span-2"><Field label="Notes"><Textarea value={cost.description} onChange={(event) => setCost({ ...cost, description: event.target.value })} /></Field></div>
              </section>
            </div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button><Button type="submit" disabled={busy === 'cost'}>{busy === 'cost' ? 'Saving...' : canManage ? `Save ${expenseMode} expense` : 'Submit expense'}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkExpenseOpen} onOpenChange={setBulkExpenseOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Import expenses</DialogTitle>
            <DialogDescription>
              Download the template, add one expense per row, then upload the completed Excel file.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2 rounded-lg border p-4">
              <p className="text-sm font-medium">Step 1: Download template</p>
              <p className="text-sm text-muted-foreground">
                Expense name, amount, and expense date are required. Event name and notes are optional.
              </p>
              <Button type="button" variant="outline" onClick={downloadExpenseTemplate} disabled={downloadingExpenseTemplate}>
                <DownloadSimple className="mr-2 h-4 w-4" />
                {downloadingExpenseTemplate ? 'Downloading...' : 'Download Excel template'}
              </Button>
            </div>
            <div className="space-y-2 rounded-lg border p-4">
              <p className="text-sm font-medium">Step 2: Upload completed template</p>
              <Input
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={uploadExpenseWorkbook}
                disabled={uploadingExpenses}
              />
              {uploadingExpenses && <p className="text-sm text-muted-foreground">Uploading expenses...</p>}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setBulkExpenseOpen(false)} disabled={uploadingExpenses}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(detailRecord)} onOpenChange={(open) => {
        if (!open) {
          setDetailRecord(null);
          setDetailMode('view');
          setRequestNote('');
        }
      }}>
        <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{detailMode === 'edit' ? 'Edit future expense' : detailRecord?.title || 'Expense details'}</DialogTitle>
            <DialogDescription>{detailMode === 'edit' ? 'Update the planned expense details before recording the remaining payment.' : detailRecord?.record_type === 'team_claim' ? 'Team claim details for manager review.' : 'Expense details, document, automation, and payment state.'}</DialogDescription>
          </DialogHeader>
          {detailRecord && (
            <div className="space-y-4 py-2">
              {canEditDetails(detailRecord) && detailMode === 'edit' && (
                <div className="rounded-xl border p-4">
                  <p className="text-sm font-medium">{canManage ? 'Edit expense details' : 'Complete missing details'}</p>
                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    <Field label="Title"><Input value={detailDraft.title} onChange={(event) => setDetailDraft({ ...detailDraft, title: event.target.value })} /></Field>
                    <Field label="Amount" required={!canManage && detailRecord.status === 'needs_information'}><Input type="number" min="0" step="0.01" value={detailDraft.amount} onChange={(event) => setDetailDraft({ ...detailDraft, amount: event.target.value })} /></Field>
                    <Field label="Category"><Select value={detailDraft.category_id} onChange={(value) => setDetailDraft({ ...detailDraft, category_id: value })} options={[['', 'Uncategorized'], ...categoryOptions.map((item) => [item.id, item.name])]} /></Field>
                    <Field label="Due date">
                      <EventAwareDatePicker value={detailDraft.due_date} onChange={(date) => setDetailDraft({ ...detailDraft, due_date: date })} defaultDate={todayDateKey} minDate={todayDateKey} />
                    </Field>
                    <div className="sm:col-span-2"><Field label="Notes"><Textarea value={detailDraft.description} onChange={(event) => setDetailDraft({ ...detailDraft, description: event.target.value })} /></Field></div>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {canManage && <Button type="button" variant="outline" disabled={busy === 'details'} onClick={() => saveRecordDetails()}>Save details</Button>}
                    {!canManage && detailRecord.status === 'needs_information' && (
                      <Button type="button" disabled={busy === 'details'} onClick={() => saveRecordDetails({ submitForApproval: true })}>Resend for approval</Button>
                    )}
                  </div>
                </div>
              )}
              <div className="grid gap-3 sm:grid-cols-2">
                <Detail label="Amount" value={formatMoney(detailRecord.amount, currency)} />
                <Detail label="Paid" value={`${formatMoney(detailRecord.paid_amount, currency)} paid`} />
                <Detail label={detailRecord.record_type === 'team_claim' ? 'Claimant' : 'Vendor/person'} value={getRecordPerson(detailRecord)} />
                <Detail label="Category" value={detailRecord.category?.name || 'Uncategorized'} />
                <Detail label="Status" value={getDisplayStatus(detailRecord).label} />
                <Detail label="Due date" value={detailRecord.due_date ? <DueDateText value={detailRecord.due_date} highlight={detailRecord.outstanding_amount > MONEY_ZERO} /> : 'Not set'} />
              </div>
              {detailRecord.document_url && (
                <Button asChild variant="outline">
                  <a href={resolveMediaUrl(detailRecord.document_url)} target="_blank" rel="noreferrer"><Receipt className="mr-2 h-4 w-4" /> View receipt or invoice</a>
                </Button>
              )}
              {detailRecord.automation?.auto_approved && <Badge className="bg-emerald-100 text-emerald-700">Auto approved</Badge>}
              {detailRecord.extraction && Object.keys(detailRecord.extraction).length > 0 && (
                <div className="rounded-xl border p-4">
                  <p className="text-sm font-medium">Extracted fields</p>
                  <pre className="mt-2 max-h-44 overflow-auto rounded-lg bg-muted p-3 text-xs">{JSON.stringify(detailRecord.extraction, null, 2)}</pre>
                </div>
              )}
              {canManage && detailRecord.status === 'awaiting_approval' && (
                <Field label="Message if details are missing">
                  <Textarea
                    value={requestNote}
                    onChange={(event) => setRequestNote(event.target.value)}
                    placeholder="Example: add final amount, vendor name, or clearer receipt photo"
                  />
                </Field>
              )}
              {canEditDetails(detailRecord) && detailMode !== 'edit' && (
                <div className="rounded-xl border p-4">
                  <p className="text-sm font-medium">{canManage ? 'Edit expense details' : 'Complete missing details'}</p>
                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    <Field label="Title"><Input value={detailDraft.title} onChange={(event) => setDetailDraft({ ...detailDraft, title: event.target.value })} /></Field>
                    <Field label="Amount" required={!canManage && detailRecord.status === 'needs_information'}><Input type="number" min="0" step="0.01" value={detailDraft.amount} onChange={(event) => setDetailDraft({ ...detailDraft, amount: event.target.value })} /></Field>
                    <Field label="Category"><Select value={detailDraft.category_id} onChange={(value) => setDetailDraft({ ...detailDraft, category_id: value })} options={[['', 'Uncategorized'], ...categoryOptions.map((item) => [item.id, item.name])]} /></Field>
                    <Field label="Due date">
                      <EventAwareDatePicker value={detailDraft.due_date} onChange={(date) => setDetailDraft({ ...detailDraft, due_date: date })} defaultDate={todayDateKey} minDate={todayDateKey} />
                    </Field>
                    <div className="sm:col-span-2"><Field label="Notes"><Textarea value={detailDraft.description} onChange={(event) => setDetailDraft({ ...detailDraft, description: event.target.value })} /></Field></div>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {canManage && <Button type="button" variant="outline" disabled={busy === 'details'} onClick={() => saveRecordDetails()}>Save details</Button>}
                    {!canManage && detailRecord.status === 'needs_information' && (
                      <Button type="button" disabled={busy === 'details'} onClick={() => saveRecordDetails({ submitForApproval: true })}>Resend for approval</Button>
                    )}
                  </div>
                </div>
              )}
              {payments.filter((item) => item.record_id === detailRecord.id).length > 0 && (
                <div className="rounded-xl border p-4">
                  <p className="text-sm font-medium">Payment history</p>
                  <div className="mt-3 space-y-2">
                    {payments.filter((item) => item.record_id === detailRecord.id).map((item) => (
                      <div key={item.id} className="flex items-center justify-between rounded-lg bg-muted/40 p-2 text-sm">
                        <span>{formatMoney(item.amount, currency)}</span>
                        <span className="text-muted-foreground">{item.method || 'Payment'} - {formatDate(item.paid_at)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex-wrap gap-2 sm:gap-2">
            {canManage && detailRecord && ['awaiting_approval', 'needs_information'].includes(detailRecord.status) && (
              <>
                {detailRecord.status === 'awaiting_approval' && <Button type="button" variant="outline" onClick={() => requestDetails(detailRecord, requestNote)}>Request details</Button>}
                <Button type="button" variant="outline" onClick={() => updateStatus(detailRecord, 'rejected')}>Reject</Button>
                {detailRecord.status === 'awaiting_approval' && <Button type="button" onClick={() => updateStatus(detailRecord, 'approved')}>Approve</Button>}
              </>
            )}
            {canManage && detailRecord && ['approved', 'scheduled'].includes(detailRecord.status) && detailRecord.outstanding_amount > MONEY_ZERO && (
              <>
                {Number(detailRecord.paid_amount || 0) > MONEY_ZERO && (
                  <Button type="button" variant="outline" onClick={() => declineRemaining(detailRecord)}>Decline rest</Button>
                )}
                <Button type="button" onClick={() => { openPaymentDialog(detailRecord); setDetailRecord(null); }}>Record payment</Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(paymentRecord)} onOpenChange={(open) => !open && closePaymentDialog()}>
        <DialogContent>
          <form onSubmit={submitPayment} noValidate>
            <DialogHeader>
              <DialogTitle>Record payment</DialogTitle>
              <DialogDescription>{paymentRecord?.title} - outstanding {formatMoney(paymentRecord?.outstanding_amount, currency)}</DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-5 sm:grid-cols-2">
              <Field label="Amount paid" required>
                <Input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={payment.amount}
                  onChange={(event) => setPayment({ ...payment, amount: event.target.value })}
                  aria-invalid={Boolean(paymentAmountError)}
                />
                {paymentAmountError && <p className="mt-1.5 text-xs font-medium text-rose-700">{paymentAmountError}</p>}
                {paymentOverage > 0 && (
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    {formatMoney(paymentOverage, currency)} above the outstanding amount will still be recorded.
                  </p>
                )}
              </Field>
              <Field label="Payment date">
                <EventAwareDatePicker value={payment.paid_at} onChange={(date) => setPayment({ ...payment, paid_at: date })} eventStartDate={currentEvent?.start_date} eventEndDate={currentEvent?.end_date} />
              </Field>
              <Field label="Method"><Input placeholder="Bank transfer, cash, UPI" value={payment.method} onChange={(event) => setPayment({ ...payment, method: event.target.value })} /></Field>
              <Field label="Reference"><Input value={payment.reference} onChange={(event) => setPayment({ ...payment, reference: event.target.value })} /></Field>
              <div className="sm:col-span-2"><Field label="Note"><Textarea value={payment.note} onChange={(event) => setPayment({ ...payment, note: event.target.value })} /></Field></div>
            </div>
            <DialogFooter><Button type="button" variant="outline" onClick={closePaymentDialog}>Cancel</Button><Button type="submit" disabled={!canSubmitPayment}>Record payment</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Sheet open={categoryManagerOpen} onOpenChange={setCategoryManagerOpen}>
        <SheetContent side="right" className="flex !w-[min(94vw,46rem)] !max-w-none flex-col p-0">
          <div className="border-b bg-white px-4 py-4 sm:px-5">
            <SheetHeader>
              <SheetTitle>Budget categories</SheetTitle>
              <SheetDescription>Set category limits, track actual spend, and control team approval rules.</SheetDescription>
            </SheetHeader>
            <div className="mt-4 grid gap-2 sm:grid-cols-3">
              <Metric label="Categories" value={overviewCategories.length} />
              <Metric label="Assigned" value={formatMoney(overview?.categorized_budget || 0, currency)} />
              <Metric
                label={Number(overview?.uncategorized_budget || 0) < 0 ? 'Over assigned' : 'Unassigned'}
                value={formatMoney(Math.abs(overview?.uncategorized_budget || 0), currency)}
                className={Number(overview?.uncategorized_budget || 0) < 0 ? 'text-rose-700' : ''}
              />
            </div>
            <div className="mt-3 flex flex-col gap-2 rounded-xl border bg-muted/20 px-3 py-2.5 text-sm sm:flex-row sm:items-center sm:justify-between">
              <span className={Number(overview?.uncategorized_budget || 0) < 0 ? 'font-medium text-rose-700' : 'text-muted-foreground'}>
                {Number(overview?.uncategorized_budget || 0) < 0
                  ? 'Your category limits are above the total budget.'
                  : 'Add categories to allocate the total budget.'}
              </span>
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  setCategoryManagerOpen(false);
                  setCategoryEditing(null);
                  setCategoryDialogOpen(true);
                }}
              >
                <Plus className="mr-2 h-4 w-4" /> Add category
              </Button>
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3 sm:px-5">
            <div className="space-y-3">
              {overviewCategories.map((category) => (
                <CategoryRow
                  key={category.id}
                  category={category}
                  currency={currency}
                  onEdit={(item) => {
                    setCategoryManagerOpen(false);
                    setCategoryEditing(item);
                    setCategoryDialogOpen(true);
                  }}
                  onDragStart={(item) => setDraggedCategoryId(item.id)}
                  onDragEnd={() => setDraggedCategoryId('')}
                  onDrop={reorderCategories}
                  isDragging={draggedCategoryId === category.id}
                />
              ))}
              {!overviewCategories.length && (
                <div className="rounded-2xl border border-dashed bg-white px-4 py-10 text-center">
                  <p className="font-medium text-wedding-charcoal">No categories yet</p>
                  <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
                    Add categories such as venue, catering, decor, travel, or gifts. They will appear immediately in planned and actual expense forms.
                  </p>
                  <Button
                    type="button"
                    className="mt-4"
                    onClick={() => {
                      setCategoryManagerOpen(false);
                      setCategoryEditing(null);
                      setCategoryDialogOpen(true);
                    }}
                  >
                    <Plus className="mr-2 h-4 w-4" /> Add first category
                  </Button>
                </div>
              )}
            </div>
          </div>
          <SheetFooter className="border-t bg-white px-4 py-3 sm:px-5">
            <Button type="button" variant="outline" onClick={() => setCategoryManagerOpen(false)}>Close</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{categoryEditing ? 'Edit category' : 'Add category'}</DialogTitle>
            <DialogDescription>Set the category budget and optional team auto-approval limit.</DialogDescription>
          </DialogHeader>
          {categoryEditing ? (
            <CategoryEditor category={categoryEditing} currency={currency} busy={busy === categoryEditing.id} onSave={saveCategory} onDelete={deleteCategory} />
          ) : (
            <div className="space-y-4 py-2">
              <Field label="Category name" required><Input required value={newCategory.name} onChange={(event) => setNewCategory({ ...newCategory, name: event.target.value })} /></Field>
              <Field label="Budget limit" required><Input required type="number" min="1" step="0.01" value={newCategory.approved_amount} onChange={(event) => setNewCategory({ ...newCategory, approved_amount: event.target.value })} /></Field>
              <Field label="Auto-approve team claims up to">
                <Input type="number" min="0" placeholder="Leave blank to require approval" value={newCategory.team_auto_approve_limit} onChange={(event) => setNewCategory({ ...newCategory, team_auto_approve_limit: event.target.value })} />
              </Field>
              <DialogFooter><Button type="button" variant="outline" onClick={() => setCategoryDialogOpen(false)}>Cancel</Button><Button type="button" onClick={createCategory} disabled={!canCreateCategory}>Add category</Button></DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function Detail({ label, value }) {
  return (
    <div className="rounded-xl border bg-muted/30 p-3">
      <p className="text-xs font-semibold uppercase text-muted-foreground">{label}</p>
      <p className="mt-1 text-sm font-medium">{value}</p>
    </div>
  );
}
